import {
  CreditCardOutlined,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { EmailButton, EmailSendModal } from '@components';
import { L } from '@lib';
import { useDemoGuard, useView } from '@hooks';
import { InvoiceStatus } from '@models';
import { Invoice, NotificationService } from '@services';
import { Utils } from '@utils';
import { Button, Col, Divider, Row, Spin, Tag, Tooltip, Typography } from 'antd';
import { SharedCard } from './SharedCard';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface IProps {
  invoice?: Invoice;
  invoiceSubtotal: number;
  invoiceVat: number;
  invoiceTotal: number;
  creditTotal: number;
  refundTotal: number;
  refundPaidTotal: number;
  refundRequestedTotal: number;
  paymentTotal: number;
  dueTotal: number;
  onPayClick?: () => void;
  children?: React.ReactNode;
}

export const InvoiceDetailCard: React.FC<IProps> = ({
  invoice,
  invoiceSubtotal = 0,
  invoiceVat = 0,
  invoiceTotal = 0,
  creditTotal = 0,
  //refundTotal = 0,
  refundPaidTotal = 0,
  //refundRequestedTotal = 0,
  paymentTotal = 0,
  dueTotal = 0,
  onPayClick,
  children,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [email, setEmail] = useState(invoice?.customer?.email?.trim() || '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailButtonLoading, setEmailButtonLoading] = useState(false);

  const { isMobile } = useView();
  const location = useLocation();
  const isCurrentPathInvoice = location.pathname === `/invoices/${invoice?.id}`;

  // leave it as is. if we find to properly pass multiple conditions, we can refactor this with a prop
  useEffect(() => {
    // Reset loading state when component mounts or invoice changes
    if (!invoice) {
      setIsLoading(true);
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);

      // Cleanup timer on unmount or dependency change
      return () => clearTimeout(timer);
    } else {
      // If invoice exists, no need to show loading
      setIsLoading(false);
    }
    setEmail(invoice?.customer?.email?.trim() || '');
    setEmailError(null);
  }, [invoice]);

  const validateEmail = (val: string) => {
    if (!val) return 'Required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) return 'Invalid email address';
    return null;
  };

  const handleEmailChange = (val: string) => {
    setEmail(val);
    setEmailError(validateEmail(val));
  };

  const { guard } = useDemoGuard();

  const handleEmailReceipt = useMemo(
    () =>
      guard(() => {
        if (!invoice) return;
        if (!invoice.customer) return;

        setEmail(invoice.customer?.email?.trim() || '');
        setEmailError(null);
        setModalVisible(true);
      }),
    [invoice, guard]
  );

  const handleSendEmail = useCallback(() => {
    if (!invoice) return;
    if (!invoice.customer) return;

    const error = validateEmail(email);
    setEmailError(error);
    if (error) return;

    setEmailButtonLoading(true);
    NotificationService.sendCustomerEmailReceipt(invoice.id, email).then(() => {
      setModalVisible(false);
      setEmailButtonLoading(false);
    });
  }, [email, invoice]);

  if (!invoice) {
    return (
      <SharedCard
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Row gutter={[{ xs: 0, sm: 0, md: 32 }, 5]} style={{ marginBottom: 16, flex: 2 }}>
          <Col span={24}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '48px 0',
                position: 'relative',
              }}
            >
              {isLoading ? (
                // Loading State - shows for 2 seconds
                <>
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 48, color: '#1890ff' }} spin />}
                    size="large"
                  />
                  <Typography.Text
                    strong
                    style={{
                      marginTop: '16px',
                      color: 'rgba(0, 0, 0, 0.65)',
                      fontSize: '16px',
                    }}
                  >
                    Loading invoice details...
                  </Typography.Text>
                </>
              ) : (
                // Empty State - shows after 2 seconds
                <>
                  <FileTextOutlined style={{ fontSize: '64px', color: '#91d5ff' }} />
                  <Typography.Text
                    strong
                    style={{
                      marginTop: '16px',
                      color: 'rgba(0, 0, 0, 0.85)',
                      fontSize: '18px',
                    }}
                  >
                    Invoice details not yet available
                  </Typography.Text>
                  {children && <div style={{ marginTop: '16px' }}>{children}</div>}
                </>
              )}
            </div>
          </Col>
        </Row>
      </SharedCard>
    );
  }

  // Return the full invoice card when invoice exists
  return (
    <>
      <SharedCard
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Row align="middle" gutter={8} wrap={false} style={{ width: '100%' }}>
          {!isMobile && (
            <Col>
              <FileTextOutlined style={{ fontSize: 18, color: '#595959' }} />
            </Col>
          )}
          <Col>
            {isCurrentPathInvoice ? (
              <Typography.Text strong style={{ fontSize: 15 }}>
                {invoice?.number}
              </Typography.Text>
            ) : (
              <Link
                to={`/invoices/${invoice?.id}`}
                target="_blank"
                style={{ fontWeight: 600, fontSize: 15 }}
              >
                {invoice?.number}
              </Link>
            )}
          </Col>
          <Col>
            <Tag
              bordered={false}
              color={Utils.getStatusTagColor(invoice?.status ?? '')}
              style={{
                borderRadius: 10,
                fontWeight: 500,
                fontSize: 11,
                padding: '2px 8px',
                lineHeight: '18px',
              }}
            >
              {invoice?.status}
            </Tag>
          </Col>
          {isMobile && dueTotal > 0 && invoice?.status === InvoiceStatus.Unpaid && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button
                icon={<CreditCardOutlined />}
                type="primary"
                size="small"
                block
                onClick={onPayClick}
                onMouseUp={(e) => e.currentTarget.blur()}
              >
                {L('Pay')}
              </Button>
            </Col>
          )}
          {!isMobile && (
            <>
              <Col flex="auto" />
              <Col style={{ paddingRight: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
                {invoice && invoice.number && (
                  <Tooltip title="Invoice preview">
                    <Link to={`/invoice/${invoice?.id}`} target="_blank">
                      <Button type="default" icon={<EyeOutlined />} />
                    </Link>
                  </Tooltip>
                )}
                <Tooltip title="Email receipt to customer">
                  <EmailButton onClick={handleEmailReceipt} iconOnly />
                </Tooltip>
                {dueTotal > 0 && invoice?.status === InvoiceStatus.Unpaid && (
                  <Tooltip title="Record a payment">
                    <Button
                      icon={<CreditCardOutlined />}
                      type="primary"
                      onClick={onPayClick}
                      onMouseUp={(e) => e.currentTarget.blur()}
                    />
                  </Tooltip>
                )}
              </Col>
            </>
          )}
        </Row>
        {isMobile && (
          <Row align="middle" style={{ marginTop: 8, gap: 6 }}>
            {invoice && invoice.number && (
              <Link to={`/invoice/${invoice?.id}`} target="_blank" style={{ flex: 1 }}>
                <Button type="default" icon={<EyeOutlined />} size="small" block>
                  {L('Preview')}
                </Button>
              </Link>
            )}
            <div style={{ flex: 1 }}>
              <EmailButton onClick={handleEmailReceipt} showText size="small" block />
            </div>
          </Row>
        )}
        <Divider style={{ margin: '12px 0' }} />
        {[
          { label: 'Amount', value: Utils.formatCurrency(invoiceSubtotal) },
          { label: 'VAT', value: Utils.formatCurrency(invoiceVat) },
          { label: 'Credited', value: Utils.formatCurrency(creditTotal) },
          { label: 'Refunded', value: Utils.formatCurrency(refundPaidTotal) },
          { label: 'Paid', value: Utils.formatCurrency(paymentTotal) },
          {
            label: 'Balance',
            value: Utils.formatCurrency(dueTotal),
            highlight: true,
            color: dueTotal > 0 ? '#cf1322' : '#389e0d',
          },
        ].map((item) => (
          <Row
            key={item.label}
            justify="space-between"
            align="middle"
            style={{
              padding: '6px 0',
            }}
          >
            <Col>
              <span style={{ fontWeight: 500, color: '#8c8c8c', fontSize: 13 }}>{item.label}</span>
            </Col>
            <Col>
              <span
                style={{
                  fontWeight: item.highlight ? 600 : 500,
                  color: item.color,
                }}
              >
                {item.value}
              </span>
            </Col>
          </Row>
        ))}

        {/* TOTAL Footer */}
        <Row
          justify="space-between"
          align="middle"
          style={{
            marginTop: 'auto',
            borderTop: '2px solid #f0f0f0',
            paddingTop: 8,
            paddingBottom: 4,
          }}
        >
          <Col>
            <span
              style={{
                fontWeight: 600,
                color: '#8c8c8c',
                fontSize: 13,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Total
            </span>
          </Col>
          <Col>
            <span style={{ fontWeight: 700, fontSize: 16 }}>
              {Utils.formatCurrency(invoiceTotal)}
            </span>
          </Col>
        </Row>
      </SharedCard>
      <EmailSendModal
        open={modalVisible}
        title={`Email Invoice ${invoice?.number} receipt`}
        emailValue={email}
        setEmailValue={handleEmailChange}
        emailError={emailError}
        onConfirm={handleSendEmail}
        onCancel={() => {
          setEmail(invoice?.customer?.email?.trim() || '');
          setEmailError(null);
          setEmailButtonLoading(false);
          setModalVisible(false);
        }}
        confirmText="Send"
        okButtonProps={{ disabled: !!emailError, loading: emailButtonLoading }}
      />
    </>
  );
};
